import React, { useRef, useEffect } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import CaseStudyCards from "./CaseStudyCards";
import { CaseStudy } from "./types";

interface CaseStudyAccordionViewProps {
  caseStudies: CaseStudy[];
  activeTab: string;
  setActiveTab: (id: string) => void;
}

const CaseStudyAccordionView = ({
  caseStudies,
  activeTab,
  setActiveTab
}: CaseStudyAccordionViewProps) => {
  const accordionRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const handleAccordionChange = (value: string) => {
    setActiveTab(value);
    
    // If opening an accordion item, scroll it into view after a brief delay
    if (value && accordionRefs.current[value]) {
      setTimeout(() => {
        accordionRefs.current[value]?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  };

  return (
    <Accordion type="single" collapsible value={activeTab} onValueChange={handleAccordionChange} className="w-full max-w-7xl mx-auto px-4 md:px-6 space-y-4 md:space-y-6">
      {caseStudies.map(study => (
        <AccordionItem 
          key={study.id} 
          value={study.id} 
          className="border-0 rounded-2xl overflow-hidden"
          ref={(el) => { accordionRefs.current[study.id] = el; }}
        >
          <AccordionTrigger className="p-0 hover:no-underline w-full">
            <div className="w-full max-w-none rounded-2xl overflow-hidden bg-gradient-to-br from-cyanPrimary to-cyanLight
              text-white relative transition-all duration-300 min-h-[160px] md:min-h-[180px] 
              shadow-lg ring-2 ring-cyanPrimary/30">
              {/* Background decoration */}
              <div className="absolute bottom-0 right-0 w-40 h-40 bg-white/5 rounded-full -mr-10 -mb-10"></div>
              <div className="absolute top-0 left-0 w-24 h-24 bg-white/5 rounded-full -ml-10 -mt-10"></div>
              
              <div className="p-6 md:p-8 relative z-10 h-full flex flex-col justify-center">
                <span className="inline-block px-3 py-1.5 bg-white/20 rounded-full text-sm font-medium uppercase tracking-wide mb-4">
                  {study.category}
                </span>
                <h3 className="text-xl md:text-2xl font-bold text-black mb-2">
                  {study.id === "ux-design" ? "Making Sense of the Signals" : study.id === "product-strategy" ? "Turning Vision Into Value" : "From Zero to Launch in 4 Months"}
                </h3>
                <p className="text-base md:text-xl text-white/90 font-normal">
                  {study.id === "ux-design" ? "Everest Effect – Impact Data Platform" : study.id === "product-strategy" ? "Merity – Professional Development Platform" : "ORKA Finance – Commercial Solar Lender"}
                </p>
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="pt-6 pb-0">
            <div className="bg-white py-8 px-4 md:px-6 shadow-inner rounded-lg">
              <CaseStudyCards study={study} />
            </div>
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

export default CaseStudyAccordionView;
